import { useTranslation } from 'react-i18next';

import { type CompanyFormValues, type CompanyInput } from './useAddEditCompanyFormConfig';
import { type UseToggle } from '@/shared/hooks/UseToggle';

import { Language } from '~/common/enums';
import { trpc } from '@/config/trpc';

import { useCreateCompany } from '@/mutations';
import useHandleCreateCompanyError from '@/shared/hooks/useHandleCreateCompanyError';
import useNotification from '@/shared/hooks/UseNotification';

import AddEditCompanyForm from './AddEditCompanyForm';
import KpcModal from '@/shared/components/2.0/layout/KpcModal';

type Props = {
  company?: CompanyInput;
  isContractor?: boolean;
  toggle: UseToggle;
  onSuccess?: () => void;
};

const AddEditCompanyModal: React.FC<Props> = ({
  company,
  isContractor = false,
  toggle,
  onSuccess = () => {},
}) => {
  const { t } = useTranslation();
  const { sendSuccessMessage } = useNotification();
  const handleCreateCompanyError = useHandleCreateCompanyError();

  const { mutateAsync: addCompanyAsync, isLoading: isAddingCompany } = useCreateCompany();
  const { mutateAsync: updateCompanyAsync, isLoading: isUpdatingCompany } =
    trpc.company.update.useMutation();
  const { mutateAsync: updateSecondaryLanguages, isLoading: isUpdatingSecondaryLanguages } =
    trpc.company.contractor.updateSecondaryLanguages.useMutation();
  const type = t('company');

  const onSubmit = async (values: CompanyFormValues) => {
    const { language, name, companyId, directDebitEnabled, addresses, ...rest } = values;

    const requestBody = {
      addresses: addresses.filter((x) => !!x.addressLine?.trim()),
      companyId: companyId || '',
      directDebit: directDebitEnabled,
      language: language || Language.English,
      name: name || '',
      ...rest,
    };

    try {
      let message;

      if (requestBody.companyId) {
        message = t('typeSuccessfullyUpdated', { type });

        if (isContractor) {
          await updateSecondaryLanguages({
            contractorId: requestBody.companyId,
            secondaryLanguages: requestBody.secondaryLanguages,
          });
        }

        await updateCompanyAsync(requestBody);
      } else {
        message = t('typeSuccessfullyAdded', { type });

        await addCompanyAsync(requestBody);
      }

      sendSuccessMessage(message);
      onSuccess();
      toggle.hide();
    } catch (error: any) {
      handleCreateCompanyError(error, requestBody.vatNumber);
    }
  };

  const title = (() => {
    if (company?.id) {
      return t('updateType', { type });
    }

    return t('addType', { type });
  })();

  return (
    <KpcModal className="w-3/4 2xl:w-1/2" title={title} toggle={toggle}>
      <AddEditCompanyForm
        onSubmit={onSubmit}
        onCancel={toggle.hide}
        company={company}
        isContractor={isContractor}
        isSubmitting={isAddingCompany || isUpdatingCompany || isUpdatingSecondaryLanguages}
      />
    </KpcModal>
  );
};

export default AddEditCompanyModal;
